/**
 * StatusBuckets — the four counts above a device list, each one a filter.
 *
 * WHY FOUR AND NOT ONE HEALTH NUMBER
 * A single "94% healthy" tells an operator nothing they can act on. Four buckets each name a
 * different next action:
 *
 *   Offline    it was up and now it is not        -> go look at it
 *   Degraded   it is up but carries an open alert -> triage it
 *   Online     up and clean                       -> nothing to do
 *   Never seen registered, never contacted us     -> it is stock, not an outage
 *
 * The last one is the reason this component exists. Until now "never contacted" was folded
 * into offline, so the offline count was a mix of real outages and paperwork and you could
 * not tell which without opening every row. That cost real time on 2026-09-10.
 *
 * THE TILES ARE BUTTONS, NOT BADGES. A number you cannot click is a number you have to act on
 * somewhere else; the tile that tells you three devices are down is the natural place to ask
 * to see those three. Clicking the active tile clears the filter.
 *
 * THE COUNTS ARE GIVEN, NOT DERIVED. This component used to ship a countByStatus(rows)
 * helper, and every caller fed it the rows it had already fetched — one page of a list
 * ordered `last_seen DESC NULLS LAST`, which sorts offline and never-seen devices past the
 * end of that page. A 300-device customer could therefore be shown "0 Offline" with forty
 * APs down. The helper is gone rather than merely unused: it was the obvious thing to reach
 * for and it was wrong every time the fleet outgrew a page. Counts come from
 * GET /api/v2/devices/status-counts (useFleetStatusCounts), which counts the whole scope.
 */
import type { DeviceStatus } from "@/types/domain";
import type { ScopeLabel as ScopeLabelData } from "@/lib/tileScope";
import { ScopeLabel } from "@/components/data/ScopeLabel";
import { cn } from "@/lib/cn";

/** Ordered worst-first: the bucket that needs attention is the one you read first. */
const BUCKETS: {
  status: DeviceStatus;
  label: string;
  /** Tailwind text colour from the status ramp. Grey for dormant — it is not on the ramp. */
  tone: string;
  hint: string;
}[] = [
  {
    status: "offline",
    label: "Offline",
    tone: "text-critical",
    hint: "Was in contact and is not now.",
  },
  {
    status: "degraded",
    label: "Degraded",
    tone: "text-warn",
    hint: "Reachable, but carrying an open alert.",
  },
  {
    status: "online",
    label: "Online",
    tone: "text-healthy",
    hint: "Reachable and clean.",
  },
  {
    status: "dormant",
    label: "Never seen",
    tone: "text-ink-muted",
    hint: "Registered but has never contacted the controller. Not an outage.",
  },
];

export function StatusBuckets({
  counts,
  active,
  onSelect,
  className,
  scope,
}: {
  counts: Record<string, number>;
  /** What the four tiles count (OP#1787, lib/tileScope). One caption for the row, referenced
   *  by every tile via aria-describedby, so each tile announces the scope it counts without
   *  repeating the same line four times in a compact row. */
  scope?: ScopeLabelData | null;
  /** The currently filtered status, or "" for none. */
  active: string;
  onSelect: (status: string) => void;
  className?: string;
}) {
  const scopeId = scope ? "status-buckets-scope" : undefined;
  return (
    <div className={cn("mb-3", className)}>
      <ScopeLabel label={scope ?? null} id={scopeId} className="mb-1" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {BUCKETS.map((b) => {
          const isActive = active === b.status;
          const count = counts[b.status] ?? 0;
          return (
            <button
              key={b.status}
              type="button"
              title={scope ? `${b.hint} ${scope.title}` : b.hint}
              aria-pressed={isActive}
              aria-describedby={scopeId}
              // Toggle: clicking the active tile clears the filter rather than re-applying it.
              onClick={() => onSelect(isActive ? "" : b.status)}
              className={cn(
                "md-card md-lift min-h-touch px-3 py-2 text-left transition-colors",
                "hover:bg-surface-2 focus-visible:shadow-focus focus-visible:outline-none",
                // The active tile is marked with the BRAND ring, not a status colour: the ramp
                // means device state, and reusing it for "selected" would make a selected
                // Online tile and a healthy device the same visual statement.
                isActive && "ring-2 ring-brand",
              )}
            >
              <div className={cn("text-2xl font-semibold tabular-nums", b.tone)}>{count}</div>
              <div className="text-xs text-ink-muted">{b.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
